/**
 * UserSubscriptionsEditor — per-user protocol subscriptions (ADR 116 Tier 3),
 * shared by AIM and SMS.
 *
 * Two dropdowns per protocol (Alert / Drill), each with up to five states:
 *   Role default · <resolved>   ← no override; the rubric resolution
 *   Unsubscribed                ← explicit per-user opt-out (Tier 3)
 *   Alert Start / 1st / 2nd     ← explicit per-user level
 *
 * The two escalation levels are ADR-117 Mechanism B and only render
 * when the host passes `escalationEnabled` (the `alert_escalation` flag).
 *
 * Presentational: `rows`, loading/mutating flags, the `canManage` permission,
 * the optional site list + selection, and the set/clear handlers all come in
 * via props. The host adapter owns the data hooks (`useUserSubscriptions` +
 * the override mutations) and error toasts — the lib has neither.
 *
 * Server-enforced rules mirrored here for affordance (the API would 400/403
 * anyway): emergency can't be unsubscribed on the Alert axis; `follow`/`group`
 * locked types render without dropdowns; without `canManage`, Unsubscribed is
 * hidden on both axes.
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Center,
  Flex,
  Portal,
  Select,
  Spinner,
  Text,
  createListCollection,
} from '@chakra-ui/react';
import { DataTable, type Column } from '@themes/aegix';
import type {
  SiteOption,
  UserSubscriptionOverridePayload,
  UserSubscriptionRow,
} from '../../types';
import {
  ROLE_DEFAULT,
  UNSUBSCRIBED,
  axisValue,
  buildCollection,
  lockCopy,
} from './helpers';

export interface UserSubscriptionsEditorProps {
  /** Subscription rows for the effective site (host-fetched). `follow`-locked
   *  rows are filtered out here for display. */
  rows: UserSubscriptionRow[];
  loading?: boolean;
  /** Dims the table + blocks input while a mutation is in flight. */
  isMutating?: boolean;
  /** `accounts.can_manage_subscription` — gates the Unsubscribed option. */
  canManage?: boolean;
  /**
   * ADR-117 Mechanism B (`alert_escalation` feature flag). When
   * false the "1st / 2nd Escalation" options are dropped from BOTH axes and
   * an already-stored level 1/2 renders clamped to "Alert Start".
   *
   * Defaults to `false` — fail closed. Every mount point is a host adapter
   * reading its app's `useFeature`, and an absent/unresolved flag must read
   * as OFF, so the safe default is "hidden". All three current adapters (AIM
   * `ProfileSiteSubscriptions`, SMS `ProfileSubscriptionsEditor` +
   * `AdminSubscriptionsEditor`) pass it explicitly; a future adapter that
   * forgets hides escalation rather than leaking it.
   */
  escalationEnabled?: boolean;
  /**
   * Candidate sites for the site selector. With ≥1 entry the editor
   * scopes to one site at a time; ≥2 shows a switcher. The host refetches
   * `rows` for `selectedSchoolId`.
   */
  schools?: SiteOption[];
  /** Controlled selected site; the host also feeds it to the data hook. */
  selectedSchoolId?: number | null;
  onSchoolChange?: (schoolId: number) => void;
  onSetOverride: (
    alertTypeId: number,
    body: UserSubscriptionOverridePayload,
  ) => void;
  onClearOverride: (alertTypeId: number) => void;
}

export function UserSubscriptionsEditor({
  rows,
  loading = false,
  isMutating = false,
  canManage = false,
  escalationEnabled = false,
  schools,
  selectedSchoolId,
  onSchoolChange,
  onSetOverride,
  onClearOverride,
}: UserSubscriptionsEditorProps) {
  const { t } = useTranslation();
  const siteScoped = schools != null && schools.length > 0;
  const showSiteSwitcher = schools != null && schools.length > 1;

  const siteCollection = useMemo(
    () =>
      createListCollection({
        items: (schools ?? []).map((s) => ({
          label: s.name,
          value: String(s.id),
        })),
      }),
    [schools],
  );

  // `follow` types are operational workflows (reunification), not subscription
  // decisions — hide them. Group-restricted types stay visible but locked.
  const visibleRows = useMemo(
    () => rows.filter((r) => r.locked !== 'follow'),
    [rows],
  );

  const handleAxisChange = (
    row: UserSubscriptionRow,
    axis: 'alert' | 'drill',
    value: string,
  ) => {
    const alertTypeId = row.alert_type.id;
    if (value === ROLE_DEFAULT) {
      // Whole-row revert — both axes return to the rubric resolution.
      onClearOverride(alertTypeId);
      return;
    }
    const level = value === UNSUBSCRIBED ? null : Number(value);
    const body =
      axis === 'alert'
        ? { alert_min_level: level }
        : { drill_min_level: level };
    onSetOverride(alertTypeId, body);
  };

  const renderAxisSelect = (
    row: UserSubscriptionRow,
    axis: 'alert' | 'drill',
  ) => {
    const collection = buildCollection(
      row,
      axis,
      canManage,
      escalationEnabled,
      t,
    );
    return (
      <Box w="44">
        <Select.Root
          collection={collection}
          value={[axisValue(row, axis, escalationEnabled)]}
          onValueChange={(details) => {
            const v = details.value[0];
            if (v) handleAxisChange(row, axis, v);
          }}
          size="xs"
          aria-label={t('axis_subscription_for', { axis: t(axis), name: row.alert_type.name })}
          data-e2e={`user-subscription-${axis}-${row.alert_type.id}`}
        >
          <Select.HiddenSelect />
          <Select.Control>
            <Select.Trigger>
              <Select.ValueText />
            </Select.Trigger>
            <Select.IndicatorGroup>
              <Select.Indicator />
            </Select.IndicatorGroup>
          </Select.Control>
          <Portal>
            <Select.Positioner>
              <Select.Content>
                {collection.items.map((item) => (
                  <Select.Item item={item} key={item.value}>
                    <Select.ItemText>{item.label}</Select.ItemText>
                    <Select.ItemIndicator />
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Positioner>
          </Portal>
        </Select.Root>
      </Box>
    );
  };

  const columns: Column<UserSubscriptionRow>[] = [
    {
      key: 'protocol',
      label: t('protocol'),
      render: (row) => (
        <Box>
          <Flex alignItems="center" gap={2}>
            <Text fontSize="sm" color="text.primary">
              {row.alert_type.name}
            </Text>
            {row.is_override && (
              <Text
                fontSize="2xs"
                color="brand.500"
                fontWeight="medium"
                textTransform="uppercase"
                data-e2e={`user-subscription-override-badge-${row.alert_type.id}`}
              >
                {t('overridden')}
              </Text>
            )}
          </Flex>
          <Text fontSize="2xs" color="text.tertiary" textTransform="capitalize">
            {row.alert_type.urgency}
          </Text>
        </Box>
      ),
    },
    {
      key: 'alert',
      label: t('alert'),
      width: '200px',
      render: (row) =>
        row.locked ? (
          <Text fontSize="xs" color="text.tertiary" fontStyle="italic">
            {lockCopy(row.locked, t)}
          </Text>
        ) : (
          renderAxisSelect(row, 'alert')
        ),
    },
    {
      key: 'drill',
      label: t('drill'),
      width: '200px',
      render: (row) => (row.locked ? null : renderAxisSelect(row, 'drill')),
    },
  ];

  const siteSwitcher = showSiteSwitcher ? (
    <Box mb={3} maxW="xs" data-e2e="user-subscription-site-select">
      <Select.Root
        collection={siteCollection}
        value={selectedSchoolId != null ? [String(selectedSchoolId)] : []}
        onValueChange={(details) => {
          const v = details.value[0];
          if (v) onSchoolChange?.(Number(v));
        }}
        size="sm"
        aria-label={t('site')}
      >
        <Select.HiddenSelect />
        <Select.Control>
          <Select.Trigger>
            <Select.ValueText placeholder={t('select_a_site')} />
          </Select.Trigger>
          <Select.IndicatorGroup>
            <Select.Indicator />
          </Select.IndicatorGroup>
        </Select.Control>
        <Portal>
          <Select.Positioner>
            <Select.Content>
              {siteCollection.items.map((item) => (
                <Select.Item item={item} key={item.value}>
                  <Select.ItemText>{item.label}</Select.ItemText>
                  <Select.ItemIndicator />
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Positioner>
        </Portal>
      </Select.Root>
    </Box>
  ) : null;

  return (
    <Box>
      {siteSwitcher}
      {loading ? (
        <Center py={6}>
          <Spinner size="sm" />
        </Center>
      ) : visibleRows.length === 0 ? (
        <Text fontSize="sm" color="text.secondary" py={2}>
          {siteScoped
            ? t('no_protocols_available_for_this_site')
            : t('no_protocols_available_for_users_sites')}
        </Text>
      ) : (
        <Box
          opacity={isMutating ? 0.7 : 1}
          pointerEvents={isMutating ? 'none' : 'auto'}
        >
          <DataTable
            columns={columns}
            data={visibleRows}
            keyExtractor={(row) => String(row.alert_type.id)}
          />
          <Text fontSize="2xs" color="text.tertiary" mt={2}>
            {t('user_subscriptions_footnote')}
          </Text>
        </Box>
      )}
    </Box>
  );
}

export default UserSubscriptionsEditor;
